<template>
    <div>
        <b-sidebar id="sidebar-backdrop" :backdrop-variant="'dark'" backdrop shadow :visible="!!citationID" :right="true"
            no-header-close no-close-on-backdrop no-close-on-esc>
            <template #header="{ }">
                <div class="d-flex align-items-center w-100 justify-content-between">
                    <div class="d-flex align-items-center w-100 mr-2">
                        <div class="text-nowrap" v-if="currentCitationData">
                            {{ currentCitationData.record_no }}
                        </div>
                    </div>
                    <b-dropdown id="dropdown-divider" text="text" class="load-dropdown mt-0">
                        <template slot="button-content">
                            <b-icon class="dots" icon="three-dots"></b-icon>
                        </template>
                        <b-dropdown-item-button @click="showDeleteCitationModal = true">
                            <p class="text_secondary cursor-pointer mb-0 f-14">{{ $t('editClaim.delete') }}</p>
                        </b-dropdown-item-button>
                    </b-dropdown>
                </div>
            </template>
            <div class="body" v-if="currentCitationData">
                <div class="sidebar-tabs">
                    <div class="bg-white rounded">
                        <v-tabs class="team-tabs mb-0" v-model="activeTab" show-arrows>
                            <v-tab key='info'>{{ $t('editClaim.infoTab') }}</v-tab>
                            <v-tab key='documents'>{{ $t('editClaim.documentsTab') }}</v-tab>
                            <v-tab key='notes'>{{ $t('editClaim.notesTab') }}</v-tab>
                            <v-tab key="activity" v-if="showActivitySection">{{ $t('editClaim.activityTab') }}</v-tab>
                        </v-tabs>
                    </div>
                </div>
                <v-tabs-items v-model="activeTab">
                    <v-tab-item key='info'>
                        <hr class="mt-0" />
                        <div class="d-block text-center mt-4rem">
                            <b-row class="align-items-center px-4">
                                <h3 class="f-18 font-weight-bold col-12 text-left">{{ $t('safetyShared.basic') }}</h3>
                                <b-col sm="12" class="mt-3">
                                    <b-form-group :label="$t('addCitation.recordLabel')" class="text-left mb-0">
                                        <b-form-input v-model="citationPayload.record_no" disabled type="text"
                                            :placeholder="$t('addCitation.recordPlaceholder')" class="unit"
                                            required></b-form-input>
                                    </b-form-group>
                                </b-col>
                                <b-col sm="12" class="mt-3">
                                    <div class="text-left select_arrow">
                                        <label class="state">{{ $t('addCitation.statusLabel') }}</label>
                                        <b-form-select v-model="citationPayload.status" :disabled="!allowUpdateRecord"
                                            class="f-12" :options="statusOptions" @change="onStatusChange" />
                                    </div>
                                </b-col>
                                <b-col sm="12" class="text-left mt-3">
                                    <label for="citation-date-add-datepicker" class="state">{{
                                        $t('addCitation.citationDateLabel')
                                    }}</label>
                                    <date-picker v-model="citationPayload.citation_date" :disabled="!allowUpdateRecord"
                                        :formatter="momentFormat" :range-separator="' - '"
                                        @clear="citationPayload.citation_date = ''"
                                        :placeholder="$t('safetyShared.selectDate')" class="date-picker-custom"
                                        :teleport="'body'"></date-picker>
                                    <p v-if="errors && errors.citation_date" class="field-error">
                                        {{ errors.citation_date[0] }}
                                    </p>
                                </b-col>
                                <b-col sm="12" class="mt-3">
                                    <b-form-group :label="$t('addCitation.assignedToLabel')"
                                        class="text-left select_arrow label mb-0">
                                        <v-select class="bg_white w-100 f-12" v-model="citationPayload.assigned_to"
                                            :options="nameOptions" item-value="id" item-text="label"
                                            :reduce="(option) => option.id" @search="searchUsers"
                                            :disabled="!allowUpdateRecord">
                                            <span slot="no-options">
                                                {{ $t('tasksPage.searchUser') }}
                                            </span>
                                        </v-select>
                                    </b-form-group>
                                </b-col>
                                <b-col sm="12">
                                    <hr class="mt-4" />
                                    <div class="text-left select_arrow">
                                        <label class="state">{{ $t('addAccident.driverLabel') }}</label>
                                        <v-select class="bg_white w-100 f-12" v-model="citationPayload.driver"
                                            :options="driverOptions" item-value="id" item-text="label"
                                            :reduce="(option) => option.id" @search="searchDriver"
                                            :disabled="!allowUpdateRecord">
                                            <span slot="no-options">
                                                {{ $t('addAccident.searchDriver') }}
                                            </span>
                                        </v-select>
                                        <p v-if="errors && errors.driver" class="field-error">
                                            {{ errors.driver[0] }}
                                        </p>
                                    </div>
                                </b-col>
                                <b-col sm="12" class="mt-3">
                                    <div class="text-left select_arrow">
                                        <label class="state">{{ $t('addAccident.vehicleLabel') }}</label>
                                        <v-select class="bg_white w-100 f-12" v-model="citationPayload.vehicle"
                                            :options="vehicleOptions" item-value="id" item-text="label"
                                            :reduce="(option) => option.id" @search="searchVehicle"
                                            :disabled="!allowUpdateRecord">
                                            <span slot="no-options">
                                                {{ $t('addAccident.searchVehicle') }}
                                            </span>
                                        </v-select>
                                    </div>
                                </b-col>
                                <b-col sm="12" class="mt-3">
                                    <div class="text-left select_arrow">
                                        <label class="state">{{ $t('addAccident.trailerLabel') }}</label>
                                        <v-select class="bg_white w-100 f-12" v-model="citationPayload.trailer"
                                            :options="trailerOptions" item-value="id" item-text="label"
                                            :reduce="(option) => option.id" @search="searchTrailer"
                                            :disabled="!allowUpdateRecord">
                                            <span slot="no-options">
                                                {{ $t('addAccident.searchTrailer') }}
                                            </span>
                                        </v-select>
                                    </div>
                                </b-col>
                                <b-col sm="12">
                                    <hr class="mt-4" />
                                    <div class="text-left select_arrow">
                                        <label class="state">{{ $t('addCitation.divisionLabel') }}</label>
                                        <v-select class="f-12" :clearable="false" :options="allDivisions"
                                            v-model="citationPayload.division" :reduce="(option) => option.id"
                                            :disabled="!allowUpdateRecord" />
                                    </div>
                                </b-col>
                                <b-col sm="12">
                                    <hr class="mt-4" />
                                    <b-form-group :label="$t('addCitation.citationNumberLabel')" class="text-left mb-0">
                                        <b-form-input v-model="citationPayload.citation_number" type="text"
                                            :disabled="!allowUpdateRecord"
                                            :placeholder="$t('addCitation.citationNumberPlaceholder')" class="unit"
                                            required></b-form-input>
                                        <p v-if="errors && errors.citation_number" class="field-error">
                                            {{ errors.citation_number[0] }}
                                        </p>
                                    </b-form-group>
                                </b-col>
                                <b-col sm="12" class="mt-3">
                                    <b-form-group :label="$t('addCitation.descriptionLabel')" class="text-left mb-0">
                                        <b-form-textarea class="f-12" :placeholder="$t('addCitation.descriptionPlaceholder')" rows="3"
                                            v-model="citationPayload.description"></b-form-textarea>
                                    </b-form-group>
                                </b-col>
                                <b-col sm="12">
                                    <div class="text-left select_arrow mt-3">
                                        <label class="state">{{ $t('addCitation.issuedStateLabel') }}</label>
                                        <v-select class="bg_white w-100 f-12" v-model="citationPayload.issued_state"
                                            :disabled="!allowUpdateRecord" :options="states" item-value="id"
                                            item-text="label" :reduce="(option) => option.id">
                                        </v-select>
                                    </div>
                                </b-col>
                                <b-col sm="12">
                                    <div class="text-left select_arrow mt-3">
                                        <label class="state">{{ $t('addCitation.location') }}</label>
                                        <b-form-input v-model="citationPayload.location" type="text"
                                            :disabled="!allowUpdateRecord"
                                            :placeholder="$t('addCitation.locationPlaceholder')" class="unit"
                                            required></b-form-input>
                                    </div>
                                </b-col>
                                <b-col sm="12" class="mt-3">
                                    <div class="text-left mb-0">
                                        <label class="state">{{ $t('addCitation.fineAmountLabel') }}</label>
                                        <b-form-input type="text" v-mask="amountMask" :disabled="!allowUpdateRecord"
                                            :placeholder="$t('addCitation.fineAmountPlaceholder')" required
                                            v-model="citationPayload.fine_amount"></b-form-input>
                                        <p v-if="errors && errors.fine_amount" class="field-error">
                                            {{ errors.fine_amount[0] }}
                                        </p>
                                    </div>
                                </b-col>
                                <b-col sm="12" class="mt-3">
                                    <div class="text-left select_arrow">
                                        <label class="state">{{ $t('addCitation.outOfServiceLabel') }}</label>
                                        <b-form-select v-model="citationPayload.out_of_service" class="f-12"
                                            :options="outOfServiceOptions" :disabled="!allowUpdateRecord" />
                                    </div>
                                </b-col>
                            </b-row>
                            <hr class="m-4" />
                            <b-row class="px-4">
                                <h3 class="f-18 font-weight-bold col-12 text-left">{{ $t('addCitation.attorneySection') }}
                                </h3>
                                <b-col sm="12" class="text-left mb-2">
                                    <b-row>
                                        <b-col sm="12" class="text-left">
                                            <label>{{ $t('addCitation.yesLabel') }}</label>
                                            <b-form-checkbox name="check-button" class="ml-0-6rem"
                                                v-model="citationPayload.attorney" switch size="lg"
                                                :disabled="!allowUpdateRecord"></b-form-checkbox>
                                        </b-col>
                                    </b-row>
                                </b-col>
                                <b-col sm="12" v-if="citationPayload.attorney">
                                    <b-row>
                                        <b-col sm="12" class="mt-3">
                                            <b-form-group :label="$t('addCitation.attorneyNameLabel')"
                                                class="text-left mb-0">
                                                <b-form-input v-model="citationPayload.attorney_name"
                                                    :disabled="!allowUpdateRecord" type="text"
                                                    :placeholder="$t('addCitation.attorneyNamePlaceholder')"
                                                    class="unit"></b-form-input>
                                            </b-form-group>
                                        </b-col>
                                        <b-col sm="12" class="mt-3">
                                            <b-form-group :label="$t('addCitation.attorneyFirmLabel')"
                                                class="text-left mb-0">
                                                <b-form-input v-model="citationPayload.attorney_firm"
                                                    :disabled="!allowUpdateRecord" type="text"
                                                    :placeholder="$t('addCitation.attorneyFirmPlaceholder')"
                                                    class="unit"></b-form-input>
                                            </b-form-group>
                                        </b-col>
                                        <b-col sm="12" class="mt-3">
                                            <b-form-group :label="$t('addCitation.attorneyPhoneLabel')"
                                                class="text-left position-relative mb-2">
                                                <b-input-group size="lg" class="w-100" prepend="+1">
                                                    <b-form-input v-mask="phoneMask" class="f-12"
                                                        v-model="citationPayload.attorney_phone"
                                                        :disabled="!allowUpdateRecord"></b-form-input>
                                                </b-input-group>
                                                <p v-if="errors && errors.attorney_phone" class="field-error mt-2">
                                                    {{ errors.attorney_phone[0] }}
                                                </p>
                                            </b-form-group>
                                        </b-col>
                                        <b-col sm="12" class="mt-3">
                                            <b-form-group :label="$t('addCitation.attorneyEmailLabel')"
                                                class="text-left mb-0">
                                                <b-form-input v-model="citationPayload.attorney_email" type="text"
                                                    :placeholder="$t('addCitation.attorneyEmailPlaceholder')"
                                                    :disabled="!allowUpdateRecord" class="unit"></b-form-input>
                                                <p v-if="errors && errors.attorney_email" class="field-error mt-2">
                                                    {{ errors.attorney_email[0] }}
                                                </p>
                                            </b-form-group>
                                        </b-col>
                                    </b-row>
                                </b-col>
                            </b-row>
                            <hr class="m-4" />
                            <b-row class="px-4 mb-7rem">
                                <h3 class="f-18 font-weight-bold col-12 text-left">{{ $t('addCitation.courtSection') }}</h3>
                                <b-col sm="12" class="text-left mb-2">
                                    <b-row>
                                        <b-col sm="12" class="text-left">
                                            <label>{{ $t('addCitation.yesLabel') }}</label>
                                            <b-form-checkbox name="check-button" class="ml-0-6rem"
                                                v-model="citationPayload.court" :disabled="!allowUpdateRecord" switch
                                                size="lg"></b-form-checkbox>
                                        </b-col>
                                    </b-row>
                                </b-col>
                                <b-col sm="12" v-if="citationPayload.court">
                                    <b-row>
                                        <b-col sm="12" class="text-left mt-3">
                                            <label for="court-date-add-datepicker" class="state">{{
                                                $t('addCitation.courtDateLabel')
                                            }}</label>
                                            <date-picker v-model="citationPayload.court_date" :formatter="momentFormat"
                                                :range-separator="' - '" :disabled="!allowUpdateRecord"
                                                @clear="citationPayload.court_date = ''"
                                                :placeholder="$t('safetyShared.selectDate')" class="date-picker-custom"
                                                :teleport="'body'"></date-picker>
                                        </b-col>
                                        <b-col sm="12" class="mt-3">
                                            <b-form-group :label="$t('addCitation.courtNameLabel')" class="text-left mb-0">
                                                <b-form-input v-model="citationPayload.court_name"
                                                    :disabled="!allowUpdateRecord" type="text"
                                                    :placeholder="$t('addCitation.courtNamePlaceholder')"
                                                    class="unit"></b-form-input>
                                            </b-form-group>
                                        </b-col>
                                        <b-col sm="12">
                                            <div class="text-left select_arrow mt-3">
                                                <label class="state">{{ $t('addCitation.courtLocationLabel') }}</label>
                                                <b-form-input v-model="citationPayload.court_location"
                                                    :disabled="!allowUpdateRecord" type="text"
                                                    :placeholder="$t('addCitation.courtLocationPlaceholder')"
                                                    class="unit"></b-form-input>
                                            </div>
                                        </b-col>
                                        <b-col sm="12" class="mt-3">
                                            <div class="text-left select_arrow">
                                                <label class="state">{{ $t('addCitation.outcomeLabel') }}</label>
                                                <b-form-select v-model="citationPayload.outcome"
                                                    :disabled="!allowUpdateRecord" class="f-12" :options="outcomeOptions" />
                                                <p v-if="citationPayload.status === 'Closed' && !['Overturned', 'Convicted'].includes(citationPayload.outcome)"
                                                    class="field-error">{{ $t('addCitation.outcomePendingClose') }}</p>
                                            </div>
                                        </b-col>
                                        <b-col sm="12" class="text-left mt-3">
                                            <label for="resolution-date-add-datepicker" class="state">{{
                                                $t('addCitation.resolutionDateLabel')
                                            }}</label>
                                            <date-picker v-model="citationPayload.resolution_date" :formatter="momentFormat"
                                                :range-separator="' - '" @clear="citationPayload.resolution_date = ''"
                                                :placeholder="$t('safetyShared.selectDate')" :disabled="!allowUpdateRecord"
                                                class="date-picker-custom" :teleport="'body'"></date-picker>
                                            <p v-if="citationPayload.status === 'Closed' && !citationPayload.resolution_date"
                                                class="field-error">{{ $t('addCitation.resolutionDateRequiredClose') }}</p>
                                        </b-col>
                                    </b-row>
                                </b-col>
                            </b-row>
                        </div>
                    </v-tab-item>
                    <v-tab-item key='documents'>
                        <div class="d-block body mb-7rem">
                            <div class="mb-3 d-flex align-items-center justify-content-between px-4 mt-4rem">
                                <h3 class="f-18 font-weight-bold text-left px-0 mb-0">{{ $t('editClaim.documentsTab') }}
                                </h3>
                                <div class="d-flex align-items-center justify-content-end cursor-pointer"
                                    v-if="allowUpdateRecord" @click="$bvModal.show('modal-document')">
                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6.5 12V6.5V12ZM6.5 6.5V1V6.5ZM6.5 6.5H12H6.5ZM6.5 6.5H1H6.5Z"
                                            fill="#367BF6" />
                                        <path d="M6.5 12V6.5M6.5 6.5V1M6.5 6.5H12M6.5 6.5H1" stroke="#367BF6"
                                            stroke-width="1.5" stroke-linecap="round" />
                                    </svg>
                                    <p class="text_primary ml-2 mb-0 fw-600">{{ $t('editClaim.addDocument') }}</p>
                                </div>
                            </div>
                            <div class="px-4">
                                <div v-if="documentItems.length === 0"
                                    class="text-center text-muted py-4 bg-white rounded document-card-empty">
                                    {{ $t('editClaim.noDocuments') }}
                                </div>
                                <div class="document-cards-list">
                                    <div v-for="item in documentItems" :key="item.pk" class="document-card">
                                        <div class="document-card-top">
                                            <span class="document-card-title">
                                                {{ item.description && item.description !== 'null' ? item.description :
                                                    '-' }}
                                            </span>
                                            <span class="document-card-date">
                                                {{ item.date || '-' }}
                                            </span>
                                        </div>
                                        <div class="document-card-divider"></div>
                                        <div class="document-card-bottom justify-content-end">
                                            <div class="document-card-actions">
                                                <svg @click="setDocumentData(item)" class="cursor-pointer" width="20"
                                                    height="15" viewBox="0 0 20 15" fill="none"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M18.6426 6.19707C18.8726 6.48384 19 6.85642 19 7.24273C19 7.62904 18.8726 8.00161 18.6426 8.28838C17.186 10.052 13.8704 13.4854 10 13.4854C6.12957 13.4854 2.81403 10.052 1.35741 8.28838C1.12735 8.00161 1 7.62904 1 7.24273C1 6.85642 1.12735 6.48384 1.35741 6.19707C2.81403 4.4335 6.12957 1 10 1C13.8704 1 17.186 4.4335 18.6426 6.19707Z"
                                                        stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round"
                                                        stroke-linejoin="round" />
                                                    <path
                                                        d="M10.0007 10.0154C11.533 10.0154 12.7752 8.77323 12.7752 7.24091C12.7752 5.70859 11.533 4.4664 10.0007 4.4664C8.46839 4.4664 7.2262 5.70859 7.2262 7.24091C7.2262 8.77323 8.46839 10.0154 10.0007 10.0154Z"
                                                        stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round"
                                                        stroke-linejoin="round" />
                                                </svg>
                                                <svg @click="downloadDoc(item)" class="cursor-pointer" width="20"
                                                    height="20" viewBox="0 0 20 20" fill="none"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M6.53943 11.3822L10.001 14.8437L13.4625 11.3822"
                                                        stroke="#FFC800" stroke-width="1.5" stroke-linecap="round"
                                                        stroke-linejoin="round" />
                                                    <path d="M10.0001 14.8497V5.15741" stroke="#FFC800" stroke-width="1.5"
                                                        stroke-linecap="round" stroke-linejoin="round" />
                                                    <path
                                                        d="M10 19C14.9706 19 19 14.9706 19 10C19 5.02944 14.9706 1 10 1C5.02944 1 1 5.02944 1 10C1 14.9706 5.02944 19 10 19Z"
                                                        stroke="#FFC800" stroke-width="1.5" stroke-linecap="round"
                                                        stroke-linejoin="round" />
                                                </svg>
                                                <svg v-if="allowUpdateRecord" @click="setDeleteDocumentData(item.id)"
                                                    class="cursor-pointer" width="19" height="20" viewBox="0 0 19 20"
                                                    fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5"
                                                        stroke-linecap="round" stroke-linejoin="round" />
                                                    <path
                                                        d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                                                        stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                                        stroke-linejoin="round" />
                                                    <path
                                                        d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                                                        stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                                        stroke-linejoin="round" />
                                                    <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5"
                                                        stroke-linecap="round" stroke-linejoin="round" />
                                                    <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5"
                                                        stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-tab-item>
                    <v-tab-item key="notes">
                        <div class="mt-4rem">
                            <Notes :key="currentCitationData.id" :noteURL="'app/ticket/' + currentCitationData.id + '/note'"
                                :allowUpdate="allowUpdateRecord" @updateNotes="$emit('citationUpdated', true)" />
                        </div>
                    </v-tab-item>
                    <v-tab-item key="activity">
                        <div class="mt-4rem mb-6rem" v-if="showActivitySection">
                            <ActivityLogs :record="currentCitationData.record_no" :object_id="currentCitationData.id"
                                :key="currentCitationData.id" />
                        </div>
                    </v-tab-item>
                </v-tabs-items>
                <div class="sidebar-footer position-fixed">
                    <div class="d-flex align-items-center justify-content-end">
                        <div class="text-right modal-footer border-0 p-0">
                            <b-button class="mx-0 h-40px w-100px m-0" @click="resetPopup()" variant="secondary"
                                :class="hasChanged && allowUpdateRecord ? 'mr-3' : ''">{{ $t('editClaim.close')
                                }}</b-button>
                            <b-button class="mx-0 mb-0 h-40px w-100px m-0" variant="primary" @click="onUpdateCitation"
                                v-if="hasChanged && allowUpdateRecord">{{ $t('editClaim.save') }}</b-button>
                        </div>
                    </div>
                </div>
            </div>
        </b-sidebar>
        <DeleteModal v-if="showDeleteDocumentPopup" @confirm="deleteDocumentItem($event)"
            :message="`Do you want to delete this doc?`" />
        <Document v-if="currentCitationData && currentCitationData.record_no" :id="citationID"
            :allowUpdateRecord="allowUpdateRecord" @documentsUpdated="getDocumentData" :documentItem="documentData"
            @popupClose="documentData = null" :mediaData="media" :mediaDataID="mediaID" :mediaDataType="mediaType"
            :addDoc="addCitationDoc" :updateDoc="updateCitationDoc" :allowVideo="true" :driver="getDriverData"
            :recordNo="currentCitationData.record_no" :deleteDocItem="deleteDocumentItem" />
        <DeleteModal v-if="showDeleteCitationModal" @confirm="deleteCitationItem($event)" />
    </div>
</template>
<script>
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { checkEmail, checkValueExists, checkPhoneFormat } from "@/services/helper";
import * as moment from "moment-timezone";
import { mapActions, mapGetters } from "vuex";
import Notes from "../Shared/Notes";
import vSelect from "vue-select";
import Document from './Document.vue';
import "vue-select/dist/vue-select.css";
import DatePicker from 'vue2-datepicker';
import DeleteModal from "@/components/DeleteModal.vue";
import ActivityLogs from "@/components/Shared/ActivityLogs.vue"
const amountMask = createNumberMask({
    prefix: "",
    allowDecimal: true,
    includeThousandsSeparator: true,
});
export default {
    name: "EditCitation",
    props: ['citationID', 'initialTab'],
    components: { Notes, vSelect, DatePicker, DeleteModal, Document, ActivityLogs },
    data() {
        return {
            errors: {},
            previousStatus: 'New',
            defaultData: {},
            amountMask,
            nameOptions: [],
            lastRecord: null,
            showRecordField: false,
            claimNumbers: [],
            accidentID: null,
            documentItems: [],
            checkEmail,
            showDeleteCitationModal: false,
            checkValueExists,
            checkPhoneFormat,
            momentFormat: {
                stringify: (date) => {
                    return date ? moment(date).format('ll') : ''
                },
                parse: (value) => {
                    if (!value) return null
                    const formats = [
                        'll', 'LL',
                        'MMM D, YYYY', 'MMMM D, YYYY',
                        'M/D/YYYY', 'MM/DD/YYYY',
                        'M-D-YYYY', 'MM-DD-YYYY',
                        'YYYY-MM-DD', 'YYYY/MM/DD',
                        'D MMM YYYY', 'D MMMM YYYY',
                        'D/M/YYYY', 'DD/MM/YYYY',
                    ]
                    const m = moment(value, formats, true)
                    if (m.isValid()) return m.toDate()
                    const lenient = moment(value)
                    return lenient.isValid() ? lenient.toDate() : null
                }
            },
            citationPayload: {
                // Basic
                status: "New",
                division: null,
                driver: null,
                vehicle: null,
                trailer: null,
                assigned_to: null,

                // Citation details
                citation_number: "",
                citation_date: null,
                description: "",
                issued_state: null,
                location: "",
                fine_amount: "",
                out_of_service: "",

                // Attorney
                attorney: false,
                attorney_name: "",
                attorney_firm: "",
                attorney_phone: "",
                attorney_email: "",

                // Court
                court: false,
                court_date: null,
                court_name: "",
                court_location: "",
                outcome: "",
                resolution_date: null,
            },
            driverOptions: [],
            vehicleOptions: [],
            trailerOptions: [],
            states: [],
            documentData: null,
            mediaID: null,
            mediaType: null,
            currentDocumentPage: 1,
            showDeleteDocumentPopup: false,
            activeTab: "info",
            media: null,
            userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
    },
    methods: {
        ...mapActions(['setLoader', 'getCitationData', 'getCitationDocs', 'deleteCitationDocument', 'addCitationDoc', 'updateCitationDoc', 'getAllTeamMembers', 'getFilteredDrivers', 'getFilteredEquipments', 'setNotification', 'updateCitation', 'getAllCountries', 'deleteCitation']),
        async getCountries() {
            await this.getAllCountries();
            const countries = this.allCountries.map((item) => ({
                id: item.id,
                label: item.name,
            }));
            this.getStates(countries[0]);
        },
        async deleteCitationItem(status) {
            if (status) {
                try {
                    await this.deleteCitation({
                        citationID: `${this.citationID}/?company_id=${this.userCompany}`
                    })
                    await this.setNotification({
                        msg: this.$t('editCitation.citationDeleted'),
                        type: "success",
                        color: "green",
                    });
                    this.showDeleteCitationModal = false
                    this.$emit("citationUpdated", true)
                    this.resetPopup()
                }
                catch (e) {
                    console.log(e)
                }
            } else {
                this.showDeleteCitationModal = false
            }
        },
        getStates(selectedOption) {
            this.states = this.allCountries
                .filter((country) => country.id === selectedOption.id)[0]
                .state.map((item) => ({
                    id: item.id,
                    label: item.name,
                }));
            this.citationPayload.issued_state = this.states.filter(
                (state) => state.label === "-"
            )[0].id;
        },
        checkNullValues(payload) {
            let nullKeys = [];
            for (let [key, value] of Object.entries(payload)) {
                if (!value) {
                    nullKeys.push(key);
                }
            }
            return nullKeys;
        },
        async openLink(link) {
            window.open(link, "_blank");
        },
        setDeleteDocumentData(id) {
            this.showDeleteDocumentPopup = true
            this.documentID = id
        },
        setDocumentData(data) {
            this.documentData = data
            this.openDoc(data)
            this.$bvModal.show("modal-document");
        },
        async openDoc(data) {
            this.docName = data.name
            this.media = data.document
            this.mediaID = data.id
            if (data.document.includes('.pdf')) this.mediaType = 'pdf'
            else if (data.document.includes('.mp4') || data.document.includes('.mkv') || data.document.includes('.mov') || data.document.includes('.avi')) this.mediaType = 'video'
            else this.mediaType = 'image'
        },
        downloadDoc(data) {
            this.docName = data.name
            this.setLoader(true)
            fetch(data.document)
                .then(response => response.blob())
                .then(blob => {
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = `${this.docName}.${data.document.split('.').pop().split(/[#?]/)[0]}`
                    link.click();
                    link.remove()
                    this.setLoader(false)
                })
                .catch(this.setLoader(false));
        },
        async deleteDocumentItem(status, id = null) {
            if (status) {
                try {
                    this.setLoader(true)
                    await this.deleteCitationDocument({
                        citationID: this.citationID,
                        documentID: `${id ? id : this.documentID}/?company_id=${this.userCompany}`
                    })
                    await this.setNotification({
                        msg: this.$t('editAccident.docDeleted'),
                        type: "success",
                        color: "green",
                    });
                    this.getDocumentData()
                    this.documentID = null
                    this.showDeleteDocumentPopup = false
                    this.setLoader(false)
                }
                catch (e) {
                    console.log(e)
                    this.setLoader(false)
                }
            } else {
                this.documentID = null
                this.showDeleteDocumentPopup = false
            }
        },
        resetPopup() {
            this.$emit("resetEditCitation", true)
        },
        async getDocumentData() {
            this.documentItems = []
            const data = await this.getCitationDocs({ id: this.citationID })
            data.filter((doc) => !doc.gallery).sort((a, b) => a?.description?.toLowerCase().localeCompare(b?.description?.toLowerCase())).map((item) => {
                this.documentItems.push({
                    id: item.id,
                    date: moment(item.date).format('ll'),
                    description: item.description,
                    document: item.document,
                    name: `citation-${this.currentCitationData.record_no}-${item.description ? item.description : ''}`
                })
            })
        },
        async searchUsers(searchText) {
            if (searchText) {
                let options = []
                let url = `page=1&page_size=20&name=${searchText}`;
                await this.getAllTeamMembers(url);
                this.allTeamMembers.map((member) => {
                    options.push({
                        id: `${member.id}`,
                        label: member.user.first_name + ' ' + member.user.last_name,
                    });
                });
                this.nameOptions = options
                this.nameOptions = [...new Map(this.nameOptions.map(item => [item['id'], item])).values()]
            }
        },
        async searchDriver(searchText) {
            if (searchText) {
                let options = []
                const driverRecords = await this.getFilteredDrivers(searchText);
                driverRecords.forEach((x) => {
                    options.push({
                        id: x.id,
                        label: x.name,
                    });
                });
                this.driverOptions = this.driverOptions.concat(options)
                this.driverOptions = [...new Map(this.driverOptions.map(item => [item['id'], item])).values()]
            }
        },
        async searchVehicle(searchText) {
            if (searchText) {
                let options = [];
                const vehicleRecords = await this.getFilteredEquipments({ unit_no: searchText, type: "Vehicle" });
                vehicleRecords.forEach((x) => {
                    options.push({
                        id: x.id,
                        label: x.unit_no,
                    });
                });
                this.vehicleOptions = this.vehicleOptions.concat(options)
                this.vehicleOptions = [...new Map(this.vehicleOptions.map(item => [item['id'], item])).values()]
            }
        },
        async searchTrailer(searchText) {
            if (searchText) {
                let options = [];
                const trailerRecords = await this.getFilteredEquipments({ unit_no: searchText, type: "Trailer" });
                trailerRecords.forEach((x) => {
                    options.push({
                        id: x.id,
                        label: x.unit_no,
                    });
                });
                this.trailerOptions = this.trailerOptions.concat(options)
                this.trailerOptions = [...new Map(this.trailerOptions.map(item => [item['id'], item])).values()]
            }
        },
        onStatusChange(newVal) {
            // Block switching to "Closed" while the court status is unresolved — revert
            // and show a red alert explaining exactly what's missing.
            if (newVal === 'Closed' && this.isCloseInvalid) {
                this.setNotification({
                    msg: this.closeInvalidReason,
                    type: "error",
                    color: "red",
                });
                this.$nextTick(() => {
                    this.citationPayload.status = this.previousStatus;
                });
                return;
            }
            this.previousStatus = newVal;
        },
        validateClose() {
            if (!this.isCloseInvalid) return true;
            this.setNotification({
                msg: this.closeInvalidReason,
                type: "error",
                color: "red",
            });
            return false;
        },
        async onUpdateCitation() {
            if (!this.validateClose()) return;
            try {
                if (this.citationPayload.attorney_email) {
                    if (!this.checkEmail(this.citationPayload.attorney_email)) {
                        throw {
                            attorney_email: [this.$t('users.wrongFormat')],
                        };
                    }
                }
                if (this.citationPayload.attorney_phone) {
                    if (!this.checkPhoneFormat(this.citationPayload.attorney_phone)) {
                        throw {
                            attorney_phone: [this.$t('users.wrongFormat')],
                        };
                    }
                }
                const nullKeys = this.checkNullValues({
                    citation_date: this.citationPayload.citation_date,
                    driver: this.citationPayload.driver
                });
                if (nullKeys.length === 0) {
                    this.setLoader(true)
                    await this.updateCitation({
                        id: this.citationID, payload: {
                            ...this.citationPayload,
                            citation_date: this.citationPayload.citation_date ? moment(this.citationPayload.citation_date).format('YYYY-MM-DD') : null,
                            court_date: this.citationPayload.court_date ? moment(this.citationPayload.court_date).format('YYYY-MM-DD') : null,
                            resolution_date: this.citationPayload.resolution_date ? moment(this.citationPayload.resolution_date).format('YYYY-MM-DD') : null,
                            fine_amount: this.citationPayload.fine_amount ? parseFloat(this.citationPayload.fine_amount.toString().replace(/,/g, '')) : null,

                        }
                    })
                    await this.setNotification({
                        msg: this.$t('editCitation.citationUpdated'),
                        type: "success",
                        color: "green",
                    });
                    this.setLoader(false)
                    this.$emit("citationUpdated", true)
                    this.resetPopup()
                }
                else {
                    let error = {};
                    nullKeys.forEach((x) => {
                        error[x] = [this.$t('safetyShared.fieldRequired')];
                    });
                    throw error;
                }
            }
            catch (error) {
                if (typeof error === "object") {
                    this.errors = error;
                    // eslint-disable-next-line no-ex-assign
                }
                this.setLoader(false);
            }
        },
    },
    watch: {
        'citationPayload.court'(val) {
            if (val) {
                if (!this.citationPayload.outcome) this.citationPayload.outcome = 'Pending'
            } else {
                this.citationPayload.outcome = ''
            }
        },
        async citationID() {
            if (this.citationID) {
                this.activeTab = this.initialTab || 'info'
                try {
                    this.setLoader(true)
                    this.citationPayload = {
                        // Basic
                        status: "New",
                        division: null,
                        driver: null,
                        vehicle: null,
                        trailer: null,
                        assigned_to: null,

                        // Citation details
                        citation_number: "",
                        citation_date: null,
                        description: "",
                        issued_state: null,
                        location: "",
                        fine_amount: "",
                        out_of_service: "",

                        // Attorney
                        attorney: false,
                        attorney_name: "",
                        attorney_firm: "",
                        attorney_phone: "",
                        attorney_email: "",

                        // Court
                        court: false,
                        court_date: null,
                        court_name: "",
                        court_location: "",
                        outcome: "",
                        resolution_date: null,
                    },
                        this.errors = {}
                    this.defaultData = {}
                    this.driverOptions = [],
                        this.vehicleOptions = [],
                        this.trailerOptions = []
                    this.nameOptions = []
                    this.accidentOptions = []
                    await this.getCountries();
                    await this.getCitationData(this.citationID)
                    this.citationPayload = {
                        record_no: this.currentCitationData.record_no,
                        citation_number: this.currentCitationData.citation_number,
                        citation_date: moment.tz(this.currentCitationData.citation_date, this.userTimeZone).toDate(),
                        issued_state: this.currentCitationData.issued_state?.id || null,
                        driver: this.currentCitationData.driver,
                        status: this.currentCitationData.status,
                        vehicle: this.currentCitationData.vehicle,
                        trailer: this.currentCitationData.trailer,
                        division: this.currentCitationData.division,
                        assigned_to: this.currentCitationData.assigned_to,
                        description: this.currentCitationData.description,
                        location: this.currentCitationData.location,
                        fine_amount: this.currentCitationData.fine_amount,
                        out_of_service: this.currentCitationData.out_of_service,
                        attorney: this.currentCitationData.attorney,
                        attorney_name: this.currentCitationData.attorney_name,
                        attorney_firm: this.currentCitationData.attorney_firm,
                        attorney_phone: this.currentCitationData.attorney_phone,
                        attorney_email: this.currentCitationData.attorney_email,
                        court: this.currentCitationData.court,
                        court_date: this.currentCitationData.court_date ? moment.tz(this.currentCitationData.court_date, this.userTimeZone).toDate() : null,
                        court_name: this.currentCitationData.court_name,
                        court_location: this.currentCitationData.court_location,
                        outcome: this.currentCitationData.outcome,
                        resolution_date: this.currentCitationData.resolution_date ? moment.tz(this.currentCitationData.resolution_date, this.userTimeZone).toDate() : null,
                    }
                    this.defaultData = JSON.parse(JSON.stringify(this.citationPayload))
                    this.previousStatus = this.citationPayload.status
                    if (this.currentCitationData.assigned_to) {
                        this.nameOptions.push({
                            id: this.currentCitationData.assigned_to,
                            label: this.currentCitationData.assigned_to_name,
                        })
                    }
                    if (this.currentCitationData.driver) {
                        this.driverOptions.push({
                            id: this.currentCitationData.driver,
                            label: this.currentCitationData.driver_name,
                        })
                    }
                    if (this.currentCitationData.vehicle) {
                        this.vehicleOptions.push({
                            id: this.currentCitationData.vehicle,
                            label: this.currentCitationData.vehicle_name,
                        })
                    }
                    if (this.currentCitationData.trailer) {
                        this.trailerOptions.push({
                            id: this.currentCitationData.trailer,
                            label: this.currentCitationData.trailer_name,
                        })
                    }
                    this.getDocumentData()
                    this.setLoader(false)
                }
                catch (e) {
                    console.log(e)
                    this.setLoader(false)
                }
            }
        }
    },
    computed: {
        ...mapGetters([
            "userCompany",
            "allEquipments",
            "allDrivers",
            "currentCitationData",
            "allCountries",
            "rolePermissions",
            "divisions",
            "allCountries",
            "allTeamMembers"
        ]),
        getDriverData() {
            return this.currentCitationData && this.currentCitationData.driver ? this.driverOptions.filter(
                (item) => item.id === this.currentCitationData.driver
            )[0]?.label : null
        },
        phoneMask() {
            return this.citationPayload.attorney_phone?.length > 0
                ? [
                    /\d/, /\d/, /\d/, " ",
                    /\d/, /\d/, /\d/, " ",
                    /\d/, /\d/, /\d/, /\d/
                ]
                : false; // Allows empty input
        },
        showActivitySection() {
            return this.rolePermissions && this.rolePermissions['Activity Log'] && this.rolePermissions['Activity Log'][0].allow
        },
        allowUpdateRecord() {
            return this.rolePermissions && this.rolePermissions['Safety'] && this.rolePermissions['Safety'][1].allow
        },
        allowDeleteRecord() {
            return this.rolePermissions && this.rolePermissions['Safety'] && this.rolePermissions['Safety'][3].allow
        },
        hasChanged() {
            return Object.keys(this.citationPayload).filter(field => {
                if (typeof this.citationPayload[field] === 'object') return JSON.stringify(this.citationPayload[field]) !== JSON.stringify(this.defaultData[field])
                if (field === 'fine_amount') return this.checkValueExists(this.citationPayload[field])?.toString()?.replaceAll(",", "") != this.checkValueExists(this.defaultData[field])?.toString()?.replaceAll(",", "")
                else return this.checkValueExists(this.citationPayload[field]) != this.checkValueExists(this.defaultData[field])
            }).length > 0
        },
        statusOptions() {
            return [
                { value: 'New', text: this.$t('safetyShared.statusNew') },
                { value: 'Open', text: this.$t('safetyShared.statusOpen') },
                { value: 'Closed', text: this.$t('safetyShared.statusClosed') },
                { value: 'Urgent', text: this.$t('safetyShared.statusUrgent') },
            ];
        },
        boolFilterOptions() {
            return [
                { value: '', text: '' },
                { value: true, text: this.$t('safetyShared.yes') },
                { value: false, text: this.$t('safetyShared.no') },
            ];
        },
        outcomeOptions() {
            return [
                { value: '', text: '-' },
                { value: 'Pending', text: this.$t('addCitation.outcomePending') },
                { value: 'Overturned', text: this.$t('addCitation.outcomeOverturned') },
                { value: 'Convicted', text: this.$t('addCitation.outcomeConvicted') },
            ]
        },
        outOfServiceOptions() {
            return [
                { value: null, text: '-' },
                { value: true, text: this.$t('safetyShared.yes') },
                { value: false, text: this.$t('safetyShared.no') },
            ];
        },
        allDivisions() {
            return this.divisions.map((item) => {
                return {
                    id: item.id,
                    label: item.division,
                }
            })
        },
        isCloseInvalid() {
            // A citation with court involvement can't be "Closed" until the outcome is
            // resolved (Overturned/Convicted) and a resolution date is set.
            if (this.citationPayload.status !== 'Closed') return false;
            if (!this.citationPayload.court) return false;
            const resolvedOutcome = ['Overturned', 'Convicted'].includes(this.citationPayload.outcome);
            return !resolvedOutcome || !this.citationPayload.resolution_date;
        },
        closeInvalidReason() {
            // Spells out exactly why the citation can't be closed.
            const needsOutcome = !['Overturned', 'Convicted'].includes(this.citationPayload.outcome);
            const needsDate = !this.citationPayload.resolution_date;
            if (needsOutcome && needsDate) return this.$t('addCitation.citationCloseInvalid');
            if (needsOutcome) return this.$t('addCitation.outcomePendingClose');
            return this.$t('addCitation.resolutionDateRequiredClose');
        },
    }
};
</script>